/**
 * Subscription context object and `useSubscription` hook.
 *
 * `.ts` (no JSX) so `SubscriptionProvider.tsx` stays a Fast Refresh component
 * boundary. Mixing this hook with the Provider in one `.tsx` file was the
 * localhost blank-page crash: HMR remounted the provider against a new
 * AuthContext object ("useAuth must be used within an AuthProvider").
 */

import { createContext, useContext } from 'react';
import type { SubscriptionStatus } from '../types/subscription';

export interface SubscriptionContextType {
  /** Full subscription status from the API */
  subscription: SubscriptionStatus | null;
  /** Whether the subscription data is currently loading (initial load only) */
  isLoading: boolean;
  /** Whether the user has an active subscription */
  isSubscribed: boolean;
  /** Whether the user can use Auto Send (has the feature) */
  canAutoSend: boolean;
  /** Whether the user can add another contact (under limit or subscribed) */
  canAddContact: boolean;
  /** Current contact limit (null = unlimited) */
  contactLimit: number | null;
  /** Whether the user must complete contact selection before using the app */
  requiresContactSelection: boolean;
  /** Whether the user must complete onboarding before using the app */
  requiresOnboarding: boolean;
  /**
   * Whether Welcome Free vs Personal must block AppShell (PBI #1258).
   * False for pre-cutoff pending users who convert at smile checkout.
   */
  requiresWelcomePlanWall: boolean;
  /** Force-refresh subscription status from the API (bypasses staleness check) */
  refreshSubscription: () => Promise<void>;
  /**
   * Force-refresh subscription status directly from Stripe, bypassing all caches.
   * Used on the account page so users always see real-time subscription state
   * (e.g. after cancelling via customer service).
   */
  forceRefreshFromStripe: () => Promise<void>;
}

/**
 * React context object. Exported for `SubscriptionProvider` only — consumers use `useSubscription`.
 */
export const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

/**
 * Hook to access subscription context.
 * Must be used within a SubscriptionProvider.
 */
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
